import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const CookiePolicy = () => {
  useEffect(() => {
    document.title = 'Cookie Policy - Wild Thyme';
  }, []);

  return (
    <div className="min-h-screen pt-32" data-testid="cookie-policy-page">
      <section className="py-16 md:py-24 bg-[#F9F8F6]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-[#D4A373] hover:underline mb-8"
              data-testid="back-home"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <h1 className="font-serif text-4xl md:text-5xl leading-tight text-[#2C332B] mb-4">
              Cookie Policy
            </h1>
            <p className="text-[#6C757D]">Last updated: February 2026</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="space-y-10 text-[#6C757D] leading-relaxed">
            <div>
              <h2 className="font-serif text-2xl text-[#2C332B] mb-4">What Are Cookies?</h2>
              <p>
                Cookies are small text files placed on your device when you visit a
                website. They help the website recognise your device and remember
                information about your visit, such as your preferences and settings.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-[#2C332B] mb-4">How We Use Cookies</h2>
              <p>
                Wild Thyme uses cookies to ensure our website functions correctly and,
                with your consent, to understand how visitors use our site so we can
                improve it. We do not use cookies for advertising or profiling purposes.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-[#2C332B] mb-4">Types of Cookies We Use</h2>

              <div className="mt-6 overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-[#E8E4DD]">
                      <th className="text-left py-3 pr-4 text-[#2C332B] font-semibold">Category</th>
                      <th className="text-left py-3 pr-4 text-[#2C332B] font-semibold">Purpose</th>
                      <th className="text-left py-3 text-[#2C332B] font-semibold">Required?</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-4 pr-4 font-medium text-[#2C332B]">Essential</td>
                      <td className="py-4 pr-4">
                        These cookies are necessary for the website to function. They
                        include cookies that remember your cookie consent preferences.
                        No personal data is collected.
                      </td>
                      <td className="py-4">Yes</td>
                    </tr>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-4 pr-4 font-medium text-[#2C332B]">Analytics</td>
                      <td className="py-4 pr-4">
                        We use Google Analytics to collect anonymous information about
                        how visitors use our website, including which pages are visited
                        most often and how visitors navigate the site. This data helps
                        us improve our website. IP addresses are anonymised.
                      </td>
                      <td className="py-4">No (consent required)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-[#2C332B] mb-4">Essential Cookies</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-[#E8E4DD]">
                      <th className="text-left py-3 pr-4 text-[#2C332B] font-semibold">Cookie</th>
                      <th className="text-left py-3 pr-4 text-[#2C332B] font-semibold">Purpose</th>
                      <th className="text-left py-3 text-[#2C332B] font-semibold">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-4 pr-4 font-mono text-sm text-[#2C332B]">wt_cookie_consent</td>
                      <td className="py-4 pr-4">Stores your cookie consent preferences</td>
                      <td className="py-4">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-[#2C332B] mb-4">Analytics Cookies</h2>
              <p className="mb-4">
                These cookies are only set if you consent to analytics cookies. They are
                provided by Google Analytics.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-[#E8E4DD]">
                      <th className="text-left py-3 pr-4 text-[#2C332B] font-semibold">Cookie</th>
                      <th className="text-left py-3 pr-4 text-[#2C332B] font-semibold">Purpose</th>
                      <th className="text-left py-3 text-[#2C332B] font-semibold">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-4 pr-4 font-mono text-sm text-[#2C332B]">_ga</td>
                      <td className="py-4 pr-4">Distinguishes unique visitors</td>
                      <td className="py-4">2 years</td>
                    </tr>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-4 pr-4 font-mono text-sm text-[#2C332B]">_ga_*</td>
                      <td className="py-4 pr-4">Maintains session state</td>
                      <td className="py-4">2 years</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-[#2C332B] mb-4">Managing Your Preferences</h2>
              <p>
                You can change your cookie preferences at any time by clicking
                "Manage Preferences" in the cookie banner, or by clearing your browser's
                local storage and revisiting the site.
              </p>
              <p className="mt-3">
                You can also control cookies through your browser settings. Most browsers
                allow you to refuse or delete cookies. Please note that disabling
                essential cookies may affect the functionality of our website.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-[#2C332B] mb-4">Changes to This Policy</h2>
              <p>
                We may update this Cookie Policy from time to time. Any changes will be
                posted on this page with an updated revision date.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-[#2C332B] mb-4">Contact Us</h2>
              <p>
                If you have any questions about our use of cookies, please contact us at{' '}
                <a href="mailto:team@wild-thyme.co.uk" className="text-[#D4A373] hover:underline">
                  team@wild-thyme.co.uk
                </a>.
              </p>
              <p className="mt-3">
                See also our{' '}
                <Link to="/privacy-policy" className="text-[#D4A373] hover:underline">
                  Privacy Policy
                </Link>{' '}
                for more information about how we handle your personal data.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicy;
